import type { Editor } from '@tiptap/react';
import React, { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TableKit } from '@tiptap/extension-table';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import { Button, ButtonIcon } from '../../../../components';

const MenuBar = ({ editor }: { editor: Editor | null }) => {
  if (!editor) {
    return null;
  }

  return (
    <div className="sticky top-0 flex flex-wrap gap-2 border-b py-2 mb-2 bg-white z-10">
      <ButtonIcon icon={'FormatBold'} onClick={() => editor.chain().focus().toggleBold().run()} isOutline={!editor.isActive('bold')} title={'Bold'}/>
      <ButtonIcon icon={'FormatItalic'} onClick={() => editor.chain().focus().toggleItalic().run()} isOutline={!editor.isActive('italic')} title={'Italic'} />
      <ButtonIcon icon={'FormatUnderlined'} onClick={() => editor.chain().focus().toggleUnderline().run()} isOutline={!editor.isActive('underline')} title={'Underline'} />
      <ButtonIcon icon={'FormatStrikethrough'}  onClick={() => editor.chain().focus().toggleStrike().run()} isOutline={!editor.isActive('strike')} title={'Strikethrough'} />
      <ButtonIcon icon={'FormatAlignLeft'} onClick={() => editor.chain().focus().setTextAlign('left').run()} isOutline={!editor.isActive({ textAlign: 'left' })} title={'Align Left'} />
      <ButtonIcon icon={'FormatAlignCenter'} onClick={() => editor.chain().focus().setTextAlign('center').run()} isOutline={!editor.isActive({ textAlign: 'center' })} title={'Align Center'} />
      <ButtonIcon icon={'FormatAlignRight'} onClick={() => editor.chain().focus().setTextAlign('right').run()} isOutline={!editor.isActive({ textAlign: 'right' })} title={'Align Right'} />
    </div>
  );
};

const ImpactReportEditor = ({ content, save, send, status = 'idle' }: { content: string | null, save: (data: any) => void, send: (data: any) => void, status: 'idle' | 'saving' | 'sending' | 'saved' | 'sent' }) => {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [ 1, 2, 3 ] },
      }),
      TableKit.configure({
        table: { resizable: true },
      }),
      Image,
      Underline,
      Link,
      Highlight,
      TextAlign.configure({ types: [ 'heading', 'paragraph' ] }),
    ],
    content: '<p>Start typing here...</p>',
  });

  useEffect(() => {
    if (editor && content) {
      editor.commands.setContent(content);
    }
  }, [ content, editor ]);

  const saveImpactReport = () => {
    if (editor) {
      const htmlContent = editor.getHTML();
      save({ data: { content: htmlContent } });
    }
  };

  const sendImpactReport = () => {
    if (editor) {
      const htmlContent = editor.getHTML();
      send({ data: { content: htmlContent } });
    }
  };

  const saveButtonLabel = status === 'saving' ? 'Saving...' : (status === 'saved' ? 'Saved' : 'Save Edits');
  const sendButtonLabel = status === 'sending' ? 'Sending...' : (status === 'sent' ? 'Sent' : 'Send Impact Report');

  return (
    <div className={'w-full mx-auto p-4 border rounded-md shadow space-y-4'}>
      <MenuBar editor={editor} />
      <EditorContent editor={editor} className="ProseMirror border p-2 rounded" />
      <div className={'flex justify-center gap-4'}>
        <Button
          type={'submit'}
          color={'positive'}
          className={'w-1/6'}
          isOutline
          disabled={[ 'saving' ].includes(status)}
          onClick={saveImpactReport}
        >
          {saveButtonLabel}
        </Button>
        <Button
          type={'submit'}
          color={'positive'}
          className={'w-1/4'}
          disabled={[ 'sending' ].includes(status)}
          onClick={sendImpactReport}
        >
          {sendButtonLabel}
        </Button>
      </div>
    </div>
  );
};

export { ImpactReportEditor };
